import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { EditOutlined } from '@ant-design/icons';
import { Button, Card, Col, Row } from 'antd';
import { isEmpty } from 'lodash';
import { TableSecondaryText } from 'styles';

import { TableTimestamp } from 'components';
import AuditLog from 'components/AuditLog';
import RenderInGrid from 'components/Layout/RenderInGrid';
import { GridContainer, Tag } from 'components/Layout/common';
import { ImageItem } from 'components/Layout/list';
import Label from 'components/Text/Label';
import { ActionsBtnGroup, SectionHeader } from 'containers/BDO/utils';
import { IParamsWithContainId } from 'utils/types';

import BDOSettingAddEditModal from '../BDOSettingAddEditModal';
import { getBDOSetting } from '../action';

const AirMilesShow = () => {
  const params = useParams<IParamsWithContainId>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const dispatch = useDispatch();

  const { bdoSetting } = useSelector((state: any) => state.bdoSettings);

  const airMilesId = params.id;

  useEffect(() => {
    if (airMilesId) {
      dispatch(getBDOSetting(airMilesId));
    }
  }, [airMilesId, dispatch]);

  const renderInGrid = (description, content, hasContentChanged?: boolean) => {
    return (
      <RenderInGrid
        description={description}
        content={content}
        hasContentChanged={hasContentChanged}
      />
    );
  };

  const renderTags = (tags = []) => {
    if (isEmpty(tags)) {
      if (!tags.length) {
        return <Label>N.A</Label>;
      }
    }
    if (!isEmpty(tags)) {
      return tags.map(tag => {
        return tag?.id ? (
          <Tag key={tag.id}>
            <Label nomargin>{tag.name}</Label>
          </Tag>
        ) : null;
      });
    } else {
      return <Label>N.A</Label>;
    }
  };

  const handleEditClick = () => {
    setIsEdit(true);
    setIsModalOpen(true);
  };

  const handleDuplicateClick = () => {
    setIsEdit(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Card>
        <Row justify="space-between">
          <SectionHeader>Airmile Program Details</SectionHeader>
          <ActionsBtnGroup>
            <Button size="large" type="primary" ghost onClick={handleDuplicateClick}>
              Duplicate
            </Button>
            <Button size="large" type="primary" icon={<EditOutlined />} onClick={handleEditClick}>
              Edit
            </Button>
          </ActionsBtnGroup>
        </Row>
        {renderInGrid('Name:', <Label dark>{bdoSetting?.name}</Label>)}
        {renderInGrid('Description:', <Label dark>{bdoSetting?.description}</Label>)}
        {renderInGrid(
          'Loyalty Acct Currency:',
          <Label dark>{bdoSetting?.loyalty_points_name || '-'}</Label>
        )}
        {renderInGrid(
          'Loyalty Acct No Placeholder:',
          <Label dark>{bdoSetting?.loyalty_acct_placeholder || '-'}</Label>
        )}
        {renderInGrid(
          'Logo:',
          <ImageItem>
            <img src={bdoSetting?.image?.url} alt={bdoSetting?.name} />
          </ImageItem>
        )}
        {renderInGrid(
          'Conversion Rate:',
          <Label dark>{bdoSetting?.conversion_rate} Credit Card Points = 1 Airmile point</Label>
        )}
        {renderInGrid('Min. Redeem Amt.:', <Label dark>{bdoSetting?.min_redeem} points</Label>)}
        {renderInGrid(
          'Pts Redeem Increment:',
          <Label dark>{bdoSetting?.increment_amount} points</Label>
        )}
        {renderInGrid('LRS Reward Code:', <Label dark>{bdoSetting?.lrs_reward_code}</Label>)}
        {renderInGrid('Applicable Credit Cards:', renderTags(bdoSetting?.cards))}
        {renderInGrid(
          'Start & End Date:',
          <Label dark>
            <Col>
              <GridContainer columns="auto 10px auto" gap="10px" inline>
                {bdoSetting?.start_date ? (
                  <TableTimestamp timestamp={bdoSetting.start_date} />
                ) : (
                  'N/A'
                )}
                <TableSecondaryText>~</TableSecondaryText>
                {bdoSetting?.end_date ? <TableTimestamp timestamp={bdoSetting.end_date} /> : 'N/A'}
              </GridContainer>
            </Col>
          </Label>
        )}
      </Card>
      <AuditLog
        entity="bdo_transfer_request_card_item"
        id={airMilesId}
        isChanges2
        key={bdoSetting?.id}
      />
      <BDOSettingAddEditModal
        title="Airmile Program Details"
        selectedBDOSetting={bdoSetting?.id}
        modalOpen={isModalOpen}
        isEdit={isEdit}
        BDOSettingType={'Airmiles'}
        cardItemType={'airmile'}
        closeModal={handleCloseModal}
      />
    </>
  );
};

export default AirMilesShow;
